import { useCallback, useState } from 'react'

/**
 * Preferências de interface que sobrevivem ao recarregar a página.
 *
 * São escolhas de conforto — um aviso recolhido, um painel fechado —, nunca dado de fiscalização.
 * O que decide glosa vem do servidor a cada consulta; o que mora aqui é só o jeito de olhar.
 *
 * Guardado por usuário do navegador, e não no servidor: recolher um aviso não é ato administrativo
 * e não merece uma linha de auditoria nem uma coluna no banco.
 */

const PREFIXO = 'gestao2:'

/**
 * Lê e grava uma preferência booleana, tolerando navegador sem armazenamento.
 *
 * O `try` não é zelo excessivo: em janela anônima com cookies bloqueados, `localStorage` existe e
 * **lança** ao ser lido. Sem a proteção, a tela inteira quebraria por causa de um aviso recolhido —
 * trocar uma listagem de chamados por uma página branca seria um péssimo negócio.
 *
 * @param chave nome da preferência, sem o prefixo do sistema
 * @param padrao valor quando nunca foi escolhido, ou quando o navegador não guarda nada
 * @returns o valor corrente e a função que o altera
 */
export function usePreferenciaLocal(chave: string, padrao: boolean) {
  const [valor, definirValor] = useState<boolean>(() => ler(chave, padrao))

  const alterar = useCallback(
    (novo: boolean) => {
      definirValor(novo)
      escrever(chave, novo)
    },
    [chave],
  )

  return [valor, alterar] as const
}

function ler(chave: string, padrao: boolean): boolean {
  try {
    const guardado = window.localStorage.getItem(PREFIXO + chave)
    return guardado === null ? padrao : guardado === 'true'
  } catch {
    return padrao
  }
}

function escrever(chave: string, valor: boolean): void {
  try {
    window.localStorage.setItem(PREFIXO + chave, String(valor))
  } catch {
    // Sem armazenamento, a escolha vale só nesta visita. É degradação aceitável.
  }
}
